import React, {useRef, useState} from "react";
import {PermissionPlayer} from "./interfaces";
import OverlayBase from "../overlay/OverlayBase";
import SearchInput from "./SearchInput";
import ToggleRow from "./ToggleRow";
import Button from "../../../components/Button";
import {usePlayerDirectory} from "./usePlayerDirectory";

export default function AddPlayersOverlay(props: {
    existingMemberIds: Set<string>,
    onAdd: (players: PermissionPlayer[]) => void,
    hide: () => void
}) {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<Map<string, PermissionPlayer>>(new Map());

    const directory = usePlayerDirectory(search);
    const listRef = useRef<HTMLDivElement>(null);

    const available = directory.players.filter(p => !props.existingMemberIds.has(p.steamId));

    const toggle = (player: PermissionPlayer) => {
        setSelected(prev => {
            const next = new Map(prev);
            if (next.has(player.steamId)) {
                next.delete(player.steamId);
            } else {
                next.set(player.steamId, player);
            }
            return next;
        });
    };

    const onScroll = () => {
        const el = listRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
            directory.loadMore();
        }
    };

    const doAdd = () => {
        props.onAdd(Array.from(selected.values()));
        props.hide();
    };

    return (
        <OverlayBase hide={props.hide} w="w-[32rem]">
            <h2 className="text-xl font-semibold mb-4">Add players to group</h2>

            <div className="space-y-3">
                <SearchInput value={search} onChange={setSearch} placeholder="Search players..."/>

                <div ref={listRef} onScroll={onScroll} className="space-y-1 max-h-80 overflow-y-auto rst-scrollbar pr-1">
                    {available.map(p => (
                        <ToggleRow
                            key={p.steamId}
                            label={<span>{p.displayName}<span className="ml-2 text-xs opacity-70 font-mono">{p.steamId}</span></span>}
                            active={selected.has(p.steamId)}
                            onClick={() => toggle(p)}
                        />
                    ))}
                    {directory.isLoading && (
                        <p className="text-neutral-500 text-sm text-center py-3">Loading players…</p>
                    )}
                    {!directory.isLoading && available.length === 0 && (
                        <p className="text-neutral-500 text-sm text-center py-6">No players found.</p>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
                <button onClick={props.hide}
                        className="px-4 py-2 hover:bg-neutral-700 border border-neutral-500 rounded-lg">Cancel
                </button>
                <Button text={`Add ${selected.size > 0 ? selected.size + " " : ""}player${selected.size === 1 ? "" : "s"}`}
                        className="bg-green-600" onClick={doAdd} disabled={selected.size === 0}/>
            </div>
        </OverlayBase>
    )
}
